import axios from 'axios';
import {notifyError, notifySuccess} from './notifications';

const API = axios.create({
    baseURL: `${process.env.REACT_APP_PROXY_HOST || ''}/api`,
});

let refreshPromise = null;

const renewAccessToken = async () => {
    if (refreshPromise) {
        return refreshPromise;
    }

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
        throw new Error('Missing refresh token');
    }

    refreshPromise = axios
        .post(`${process.env.REACT_APP_PROXY_HOST || ''}/api/renewToken`, {refreshToken})
        .then((response) => {
            const newAccessToken = response?.data?.accessToken;
            if (!newAccessToken) {
                throw new Error('Missing renewed access token');
            }
            localStorage.setItem('accessToken', newAccessToken);
            return newAccessToken;
        })
        .finally(() => {
            refreshPromise = null;
        });

    return refreshPromise;
};

const SILENT_URL_PARTS = ['/renewToken', '/verifyToken'];

// Nastavení autorizační hlavičky pro každý request
API.interceptors.request.use((config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return config;
}, error => Promise.reject(error));

API.interceptors.response.use(
    response => {
        const method = (response?.config?.method || 'get').toLowerCase();
        const skipSuccess = response?.config?.meta?.silentSuccess
            || shouldSilenceRequest(response?.config?.url)
            || shouldSkipSuccessByUrl(method, response?.config?.url);

        if (!skipSuccess && ['post', 'put', 'patch', 'delete'].includes(method)) {
            notifySuccess(successMessageForMethod(method));
        }

        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        const statusCode = error?.response?.status;
        const hasRefreshToken = Boolean(localStorage.getItem('refreshToken'));

        if (statusCode === 401 && hasRefreshToken && !originalRequest?._retry) {
            try {
                originalRequest._retry = true;
                const accessToken = await renewAccessToken();
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                return API(originalRequest);
            } catch (renewError) {
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                return Promise.reject(renewError);
            }
        }

        const skipErrorNotification = originalRequest?.meta?.silentError
            || shouldSilenceRequest(originalRequest?.url);
        if (!skipErrorNotification) {
            notifyError(parseApiError(error));
        }

        return Promise.reject(error);
    }
);

function shouldSilenceRequest(url = '') {
    return SILENT_URL_PARTS.some(part => (url || '').includes(part));
}

function shouldSkipSuccessByUrl(method, url = '') {
    const normalizedUrl = (url || '').toLowerCase();
    if (method === 'post' && normalizedUrl.includes('/list')) {
        return true;
    }
    if (normalizedUrl.includes('/login')) {
        return true;
    }
    return false;
}

function successMessageForMethod(method) {
    if (method === 'delete') return 'Smazání proběhlo v pořádku.';
    if (method === 'post') return 'Vytvoření proběhlo v pořádku.';
    return 'Uložení proběhlo v pořádku.';
}

function parseApiError(error) {
    const data = error?.response?.data || {};

    const errorCode = firstNonEmpty(
        data.errorCode,
        data?.error?.errorCode
    );

    const errorMessage = firstNonEmpty(
        data.errorMessage,
        data?.error?.message,
        data.message,
        error?.message
    );

    if (errorCode && errorMessage) {
        return `${errorCode}: ${errorMessage}`;
    }
    if (errorCode) {
        return `${errorCode}`;
    }
    if (errorMessage) {
        return errorMessage;
    }

    return 'Neznámá chyba při komunikaci se serverem.';
}

function firstNonEmpty(...values) {
    const value = values.find(v => typeof v === 'string' && v.trim());
    return value ? value.trim() : '';
}

export default API;
